/**
 * Core functions the primitives evaluate (W1-12 ui-kit-i18n): the expression language for NumberField
 * (`Expr`, core/expr) and Phira's easing formulas and catalogue for CurveThumb (`Ease.modelY`, `Ease.catalog`,
 * core/ease).
 *
 * The app uses the real modules (the default). `UiCoreProvider` lets tests and tools inject replacements
 * (WORKPACKAGES W1-12: "NumberField and CurveThumb take injected parse/eval in tests"), e.g. a tiny expression
 * parser while core/expr is still being built, or a recording `modelY`.
 */

import { createContext, useContext, useMemo, type ReactNode } from 'react';
import { Ease } from '../core/ease';
import type { Ease as EaseValue } from '../core/ease/types';
import { Expr } from '../core/expr';
import type { ExprApi } from '../core/expr/types';

export interface UiCoreApis {
  readonly expr: ExprApi;
  readonly modelY: (e: EaseValue, x: number) => number;
  /** RPE name of an easingType ("OutQuad"), or null for unknown ids. */
  readonly easeName: (easingType: number) => string | null;
}

const DEFAULT_APIS: UiCoreApis = {
  expr: Expr,
  modelY: (e, x) => Ease.modelY(e, x),
  easeName: (id) => Ease.catalog(id)?.rpeName ?? null,
};

const CoreContext = createContext<UiCoreApis>(DEFAULT_APIS);

export interface UiCoreProviderProps {
  readonly expr?: ExprApi;
  readonly modelY?: (e: EaseValue, x: number) => number;
  readonly easeName?: (easingType: number) => string | null;
  readonly children: ReactNode;
}

export function UiCoreProvider(props: UiCoreProviderProps) {
  const parent = useContext(CoreContext);
  const { expr, modelY, easeName } = props;
  const value = useMemo<UiCoreApis>(
    () => ({
      expr: expr ?? parent.expr,
      modelY: modelY ?? parent.modelY,
      easeName: easeName ?? parent.easeName,
    }),
    [expr, modelY, easeName, parent],
  );
  return <CoreContext value={value}>{props.children}</CoreContext>;
}

export function useUiCore(): UiCoreApis {
  return useContext(CoreContext);
}
